
import {Component, Vue, Watch} from "vue-property-decorator";
import MobileList from "@/components/mobile/MobileListRelayItem.vue";
import MobileListKeyItem from "@/components/mobile/MobileListKeyItem.vue";
import KeyCreate from "@/components/keys/KeyCreate.vue";
import {Getter} from "vuex-class";
import KeyGroups from "@/views/front-office/KeyGroups.vue";
import { CONTEXT_TYPES, IContext } from "@/store/modules/context";
import ListKeyItem from "@/components/list/ListKeyItem.vue";

// Number of items loaded with infinite scroll
const ITEMS_PER_PAGE: number = 20;

@Component({
    components: {
      ListKeyItem,
        MobileListKeyItem,
        MobileList,
        KeyCreate,
        KeyGroups,
    },
})
export default class KeysMobile extends Vue {
    @Getter private currentUser: any;
    @Getter private isDemo: boolean;
    @Getter private canDoInEnvCompany: (right: string) => boolean;
  @Getter private canDoWithoutCompany: (right: string) => boolean;
    @Getter private context: IContext|null;
    @Getter private envCompanyId: number|null;

    private keys: any = [];
    private isLoading: boolean = true;
    private keysSearch: string = null;
    private pagination: any = {
        search: "",
        page: 1,
        itemsPerPage: ITEMS_PER_PAGE,
        tab: "myKeys",
    };
    private totalKeys: number = 10;
    private showCreateDialog: boolean = false;

    get isKeysTab(): boolean {
        return this.pagination.tab === "myKeys";
    }

    @Watch("pagination.tab", { deep: true })
    public handlerTab() {
        // reset search
        this.keysSearch = null;
        this.pagination.search = null;
        // If user quits keys tab, clear context
        if (!this.isKeysTab) {
          this.clearContext();
        } else {
          this.fetchFirstKeys();
        }
    }

    @Watch("envCompanyId", { immediate: true })
    public handlerEnvCompanyId() {
      if (this.currentUser && this.envCompanyId) {
        this.initKeysList();
      }
    }

    private enterKeyPress(e) {
        if (e.keyCode === 13) {
           this.searchHandler();
        }
    }

    get hasRightToCreateKey(): boolean {
        return this.envCompanyId
            ? this.canDoInEnvCompany("CREATE_KEYS")
            : this.canDoWithoutCompany("CREATE_KEYS");
    }

    get hasRightToCreateKeyGroup(): boolean {
        return this.envCompanyId
            ? this.canDoInEnvCompany("MANAGE_KEY_GROUPS")
            : this.canDoWithoutCompany("MANAGE_KEY_GROUPS");
    }

    get canCreateKey(): boolean {
        return !this.isCompanyBlocked
            && this.pagination.tab === "groups"
              ? this.hasRightToCreateKeyGroup
              : this.hasRightToCreateKey;
    }

    get allKeysFetch(): boolean {
        return this.totalKeys === this.keys?.length;
    }

    private mounted() {
        if (this.$route.params.tab && !this.isDemo) {
            this.pagination.tab = this.$route.params.tab;
        }
        this.initScroll();
    }

    private async initKeysList() {
      try {
        await this.fetchFirstKeys();
      } catch (error) {
        this.$store.commit("alerts/displayHttpError", { error });

      }
    }

    private fetchFirstKeys(): Promise<void> {
        if (!this.envCompanyId) {
            throw new Error("MISSING_ENV_COMPANY_ID");
        }
        this.pagination.page = 1;
        this.isLoading = true;
        this.keys = [];
        return this.getClassicKeys()
          .then((classicKeys) => {
              this.keys = classicKeys?.keys || [];
              this.totalKeys = classicKeys?.count || 0;
          })
          .catch((error) => {
            this.$store.commit("alerts/displayHttpError", { error });
          })
          .finally(() => {
            this.isLoading = false;
        });
    }

    private fetchNextKeys(): Promise<void> {
        // Increment page param
        this.pagination.page += 1;
        // Fetch new keys and concat
        this.isLoading = true;
        return this.getClassicKeys().then((res: any) => {
            this.keys = [
                ...this.keys,
                ...res.keys,
            ];
            // No need to update totalKeys since it shouldn't have changed
        }).finally(() => {
            this.isLoading = false;
        });
    }

    private getClassicKeys(): Promise<{ count: number, keys: any[] }> {
        return this.$store.dispatch("users/getMyKeys", {
            id: this.currentUser.id,
            query: {
              role: this.pagination?.tab === "myKeys" ? "MANAGER" : "GUEST",
              search: this.pagination.search,
              boxId: this.context?.type === CONTEXT_TYPES.BOX ? this.context.id : null,
              page: this.pagination.page,
              itemsPerPage: this.pagination.itemsPerPage,
            },
        }).then(this.formatKeysRes);
    }

    private formatKeysRes = (res: any): { count: number, keys: any[] } => ({
        count: res.count,
        keys: res.keys.map(this.formatKey),
    })

    private formatKey(key: any): any {
        // Display either current location or reserved location if the key is not in relay
        key.locationDisplayed = key.currentLocation || key.ReservedLocations?.[0];
        return key;
    }

    get isCompanyBlocked(): boolean {
        return this.currentUser?.companyId
            ? !!this.currentUser?.company?.blockedAt
            : false;
    }

    private initScroll(): void {
        window.addEventListener("scroll", async (): Promise<void> => {
            const listElm = document.querySelector("#keys-list");
            const currentScrollValue: number = document.documentElement.scrollTop;
            const threshold: number = listElm?.clientHeight - window.innerHeight - 1000;
            const isTargetPos: boolean = currentScrollValue > threshold;
            if (isTargetPos && !this.isLoading && !this.allKeysFetch && this.isKeysTab) {
                await this.fetchNextKeys();
            }
        });
    }

    private clearContext() {
        this.$store.dispatch("context/clearContext");
        if (this.isKeysTab) {
          this.fetchFirstKeys();
        }
    }

    private create(): void {
        switch (this.pagination.tab) {
            case "myKeys":
                this.showCreateDialog = true;
                break;
            case "groups":
                (this.$refs.keyGroup as HTMLFormElement)?.toggleCreateKeyGroups();
                break;
            default:
                break;
        }
    }

    private searchHandler(): void {
        switch (this.pagination.tab) {
            case "myKeys":
                this.pagination.search = this.keysSearch;
                this.fetchFirstKeys();
                break;
            case "groups":
                (this.$refs.keyGroup as HTMLFormElement).pagination.search = this.keysSearch;
                break;
            default:
                break;
        }
    }
}
